'use client';

import type { FC, HTMLAttributes } from 'react';
import React, { useState } from 'react';
import {
    MobileNavigationCloseBar,
    MobileNavigationOverlay,
} from '@paypoteam/page-components';
import Link from 'next/link';
import { useTheme } from 'next-themes';

import { cn } from '@/lib/utils';

import { LocalImage } from '@/components/atoms/LocalImage';
import { ButtonsFactory } from '@/components/molecules/ButtonsFactory';
import { MobileNavigationList } from '@/components/molecules/MobileNavigationList';
import MobileControls from '@/components/organisms/Navigation/components/mobile/MobileControls';
import type { NavigationConfigType } from '@/components/organisms/Navigation/Navigation.types';

import { handleLockScroll } from '../../utils/handleLockScroll';

interface MobileListProps
    extends HTMLAttributes<HTMLDivElement>,
        NavigationConfigType {
    location: string;
}

const MobileNavigation: FC<MobileListProps> = ({
    location,
    links,
    buttons,
    className,
    mobileControls,
}) => {
    const [isNavVisible, setIsNavVisible] = useState<boolean>(false);

    const { forcedTheme } = useTheme();

    const handleMenuToggle = (isOpen: boolean) => {
        setIsNavVisible(isOpen);
        handleLockScroll(isOpen);
    };

    return (
        <nav
            className={cn(
                'flex items-center justify-between px-6 md:px-8',
                className,
            )}
        >
            <Link
                href="/"
                // onClick={() => {
                //     Sentry.addBreadcrumb({
                //         category: 'navigation',
                //         message: 'click to menu link /',
                //         level: 'info',
                //     });
                // }}
            >
                <LocalImage
                    src={`/images/logos/${forcedTheme === 'dark' ? 'logo-white.svg' : 'logo.svg'}`}
                    alt="PayPo logo"
                    width={141}
                    height={40}
                    className="h-8 w-[112px] md:h-10 md:w-[141px]"
                />
            </Link>

            <MobileControls
                mobileControls={mobileControls}
                handleMenuOpen={() => {
                    handleMenuToggle(true);
                }}
            />

            <div
                className={cn(
                    'fixed right-0 top-[-100vh] z-[100] flex h-dvh w-screen flex-col bg-background',
                    'transition-[all] duration-500 ease-in-out md:right-[-100vw] md:top-0 md:w-[420px]',
                    isNavVisible && 'top-0 md:right-0',
                    !isNavVisible && 'md:hidden',
                )}
            >
                <MobileNavigationCloseBar
                    handleClose={() => {
                        handleMenuToggle(false);
                    }}
                />

                <div className="flex-1 overflow-y-auto pb-[172px]">
                    <MobileNavigationList
                        links={links}
                        location={location}
                        size="s"
                        variant={null}
                        navigationLinkVariant={{
                            color: 'active',
                            states: 'enable',
                        }}
                        className="px-6 md:px-8"
                        handleMenuClose={() => {
                            handleMenuToggle(false);
                        }}
                    />
                </div>

                {!!buttons && (
                    <div className="absolute bottom-0 left-0 right-0 bg-background">
                        <ButtonsFactory
                            config={buttons}
                            justifyContent="center"
                            reverseOnMobile={true}
                            className="items-stretch px-6 pb-8 pt-6 md:flex-col-reverse"
                        />
                    </div>
                )}
            </div>

            <MobileNavigationOverlay
                isVisible={isNavVisible}
                onClick={() => {
                    handleMenuToggle(false);
                }}
            />
        </nav>
    );
};

export default MobileNavigation;
